import { Box, Button, ButtonGroup, Flex, Icon, Image, useToast } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useSendListingMessage } from '../../../hooks/listings/useSendListingMessage'
import { useEffect, useState } from 'react'
import {
  selectCustomerFirstName,
  selectCustomerHasSponsor,
  selectCustomerIsLoanOfficer,
  selectCustomerIsRealEstateAgent,
  selectCustomerPhotoUri
} from '../../../store/selectors/customerProfile'
import { MessageTopicKey } from '../../../api/gqlaxy/gql/generated/graphql'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../store/selectors/sponsorship'
import * as Sentry from '@sentry/browser'
import { ConfettiTarget } from '../../shared/Confetti'

const MSG = defineMessages({
  cancel: {
    id: 'AssumablesLearnMoreModal.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'AssumablesLearnMoreModal.confirm',
    defaultMessage: 'Confirm'
  },
  assumablesDm: {
    id: 'AssumablesLearnMoreModal.assumablesDm',
    defaultMessage: 'Ask {name} if you qualify'
  },
  assumablesDmGeneral: {
    id: 'AssumablesLearnMoreModal.assumablesDmGeneral',
    defaultMessage: 'Ask {name} about these listings'
  },
  success: {
    id: 'AssumablesLearnMoreModal.success',
    defaultMessage: 'Direct message sent!'
  },
  fail: {
    id: 'AssumablesLearnMoreModal.fail',
    defaultMessage: 'Direct message failed to send.'
  }
})

/**
 * DM animation is based off EstimatedMonthlyPayment
 *
 * Goal is to eventually move this whole thing to the DS
 */
enum DMStatus {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Success = 'success',
  Fail = 'fail'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

export const AssumablesDirectMessage = () => {
  const intl = useIntl()
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const [isSending, setIsSending] = useState(false)
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)
  const [confettiCounter, setConfettiCounter] = useState(0)

  const isCustomerLO = useSelector(selectCustomerIsLoanOfficer)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const isCustomerSponsored = useSelector(selectCustomerHasSponsor)

  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerFirstName = useSelector(selectCustomerFirstName)

  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  const getTransformationStyles = (): React.CSSProperties => {
    let transformation = 'translateY(0%)'

    switch (dmStatus) {
      case DMStatus.Initial:
        break
      case DMStatus.Confirmation:
        transformation = 'translateY(-37%)'
        break
      case DMStatus.Fail:
        transformation = 'translateY(-73.5%)'
        break
      case DMStatus.Success:
        transformation = 'translateY(-73.5%)'
        break
    }

    return {
      transform: transformation
    }
  }

  useEffect(() => {
    getTransformationStyles()
  }, [isSending, dmStatus])

  const handleSubmit = async () => {
    setIsSending(true)

    try {
      await sendListingMessage({
        input: {
          topicKey: MessageTopicKey.ListingsDirectMessageToSponsor,
          message: isCustomerREA
            ? 'Can you tell me more about this listing gallery?'
            : 'I want to explore assumable mortgages, is this available to me?',
          topic: 'Assumable Loans'
        }
      })
      setDmStatus(DMStatus.Success)
    } catch (error: any) {
      setDmStatus(DMStatus.Fail)

      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: error.message || '',
          requestData: error.config?.data || {},
          requestErrors: Object.values(error.response?.data?.errors) || []
        }
      })
    } finally {
      setTimeout(() => {
        setIsSending(false)
      }, 300)
    }
  }

  return (
    <Flex direction='column' align='center' w='100%'>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position={'relative'} h={12} mb={0} overflow={'hidden'} w={'100%'}>
              <Flex
                direction={'column'}
                gap={4}
                style={getTransformationStyles()}
                transition={'.15s ease-in-out transform'}
              >
                <Flex direction='column' gap={4}>
                  <Box transition={'.15s ease-in-out transform'}>
                    <Button
                      colorScheme='secondary'
                      width='100%'
                      visibility={dmStatus === DMStatus.Initial ? 'visible' : 'hidden'}
                      isLoading={isSending}
                      variant={'outline'}
                      bg='neutral.50'
                      onClick={() => setDmStatus(DMStatus.Confirmation)}
                      tracking={{
                        guid: 'Assumables Learn More Modal',
                        descriptor: 'Initial Button Press to send message'
                      }}
                      leftIcon={
                        <Image
                          src={isCustomerSponsored ? sponsorPhoto : customerPhoto}
                          h={6}
                          w={6}
                          borderRadius='50%'
                        />
                      }
                    >
                      {isCustomerLO || isCustomerSponsored
                        ? intl.formatMessage(MSG.assumablesDm, {
                            name: isCustomerSponsored ? sponsorFirstName : customerFirstName
                          })
                        : intl.formatMessage(MSG.assumablesDmGeneral, { name: customerFirstName })}
                    </Button>
                  </Box>
                  <ButtonGroup
                    width={'100%'}
                    transition={'1s ease-in-out opacity'}
                    opacity={dmStatus === DMStatus.Fail ? 0 : '100%'}
                  >
                    <Box overflow={'hidden'} width={isSending ? 0 : '50%'} transition={'.1s ease-in-out all'}>
                      <Button
                        variant={'outline'}
                        colorScheme='secondary'
                        bg='neutral.50'
                        w={isSending ? 0 : '100%'}
                        transition={'.2s ease-in-out all'}
                        px={6}
                        tracking={{
                          guid: 'fHi5iCjZh1nsarWFHHPRf5',
                          ui_context: 'Assumables Learn More Modal',
                          descriptor: 'Cancel submission of dm'
                        }}
                        visibility={dmStatus === DMStatus.Confirmation ? 'visible' : 'hidden'}
                        onClick={() => setDmStatus(DMStatus.Initial)}
                        disabled={isSending || dmStatus === DMStatus.Confirmation}
                      >
                        {intl.formatMessage(MSG.cancel)}
                      </Button>
                    </Box>
                    <Button
                      w='100%'
                      onClick={() => handleSubmit()}
                      colorScheme='primary'
                      isLoading={isSending}
                      visibility={dmStatus === DMStatus.Confirmation ? 'visible' : 'hidden'}
                      opacity={'100% !important'}
                      disabled={dmStatus !== DMStatus.Confirmation}
                      tracking={{
                        guid: 'sXCKvqYN7iQnoys7tbKGwL',
                        ui_context: 'Assumables Learn More Modal',
                        descriptor: 'Confirm direct message submission'
                      }}
                    >
                      {intl.formatMessage(MSG.confirm)}
                    </Button>
                  </ButtonGroup>
                </Flex>
                <Flex>
                  <Button
                    w='100%'
                    variant='outline'
                    tracking={{
                      guid: '5TDGbTrJHZAL2jrUtSrRVi',
                      ui_context: 'Assumables direct message',
                      descriptor: 'Clicked on failure / success button'
                    }}
                    visibility={dmStatus === DMStatus.Success || dmStatus === DMStatus.Fail ? 'visible' : 'hidden'}
                    disabled={dmStatus === DMStatus.Confirmation || dmStatus === DMStatus.Initial}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    onClick={() => debouncedHandleFinalStatus()}
                  >
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                  </Button>
                </Flex>
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
    </Flex>
  )
}
